import { isNative, isWeb } from '@/lib/capacitor';
import { useStripePrices } from './useStripePrices';
import { useIAPPrices } from './useRevenueCat';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { isRevenueCatAvailable } from '@/lib/revenuecat';

// Unified price data type that works for both Stripe and IAP
export interface UnifiedPricesData {
  monthly: {
    id: string;
    priceFormatted: string;
  } | null;
  sixMonth: {
    id: string;
    priceFormatted: string;
    perMonthFormatted: string;
    trialDays: number;
    savingsPercent: number;
  } | null;
  yearly: {
    id: string;
    priceFormatted: string;
    perMonthFormatted: string;
    trialDays: number;
    savingsPercent: number;
  } | null;
  source: 'stripe' | 'iap';
  isLoading: boolean;
  error: Error | null;
  // Fallback info - true when native app shows web fallback
  isWebFallback: boolean;
  fallbackReason?: 'feature_disabled' | 'revenuecat_unavailable' | 'iap_error';
}

/**
 * Unified prices hook that automatically uses the right source:
 * - Web: Stripe prices
 * - Native (iOS/Android): RevenueCat IAP prices (unless feature flag is disabled)
 */
export function usePrices(): UnifiedPricesData {
  // Always call both hooks (React rules), but only one will actually fetch
  const stripePrices = useStripePrices();
  const iapPrices = useIAPPrices();
  const { isEnabled } = useFeatureFlags();

  // Check if native IAP is enabled via feature flag
  const isNativeIAPEnabled = isEnabled('feature_native_iap');
  const isRevenueCatReady = isRevenueCatAvailable();

  // Helper to build Stripe fallback response for native
  const buildWebFallback = (reason: 'feature_disabled' | 'revenuecat_unavailable' | 'iap_error'): UnifiedPricesData => {
    const data = stripePrices.data;

    if (stripePrices.isLoading) {
      return {
        monthly: null,
        sixMonth: null,
        yearly: null,
        source: 'stripe',
        isLoading: true,
        error: null,
        isWebFallback: true,
        fallbackReason: reason,
      };
    }

    return {
      monthly: data?.monthly
        ? { id: data.monthly.id, priceFormatted: data.monthlyFormatted }
        : { id: 'price_monthly', priceFormatted: '3,99 €' },
      sixMonth: data?.sixMonth
        ? {
            id: data.sixMonth.id,
            priceFormatted: data.sixMonthFormatted,
            perMonthFormatted: data.sixMonthPerMonthFormatted,
            trialDays: data.sixMonthTrialDays,
            savingsPercent: data.sixMonthSavingsPercent,
          }
        : {
            id: 'price_6month',
            priceFormatted: '14,99 €',
            perMonthFormatted: '2,50 €',
            trialDays: 7,
            savingsPercent: 37,
          },
      yearly: data?.yearly
        ? {
            id: data.yearly.id,
            priceFormatted: data.yearlyFormatted,
            perMonthFormatted: data.yearlyPerMonthFormatted,
            trialDays: data.yearlyTrialDays,
            savingsPercent: data.yearlySavingsPercent,
          }
        : {
            id: 'price_1year',
            priceFormatted: '23,99 €',
            perMonthFormatted: '2,00 €',
            trialDays: 7,
            savingsPercent: 50,
          },
      source: 'stripe',
      isLoading: false,
      error: null,
      isWebFallback: true,
      fallbackReason: reason,
    };
  };

  // Native platform handling
  if (isNative) {
    // Feature flag disabled - use web fallback
    if (!isNativeIAPEnabled) {
      return buildWebFallback('feature_disabled');
    }

    // RevenueCat not available - use web fallback
    if (!isRevenueCatReady) {
      return buildWebFallback('revenuecat_unavailable');
    }

    // Use IAP prices
    const data = iapPrices.data;

    if (iapPrices.isLoading) {
      return {
        monthly: null,
        sixMonth: null,
        yearly: null,
        source: 'iap',
        isLoading: true,
        error: null,
        isWebFallback: false,
      };
    }

    // IAP error - use web fallback
    if (iapPrices.error || !data) {
      return buildWebFallback('iap_error');
    }

    return {
      monthly: data.monthly
        ? { id: data.monthly.identifier, priceFormatted: data.monthlyFormatted }
        : null,
      sixMonth: data.sixMonth
        ? {
            id: data.sixMonth.identifier,
            priceFormatted: data.sixMonthFormatted,
            perMonthFormatted: data.sixMonthPerMonthFormatted,
            trialDays: data.sixMonthTrialDays,
            savingsPercent: data.sixMonthSavingsPercent,
          }
        : null,
      yearly: data.yearly
        ? {
            id: data.yearly.identifier,
            priceFormatted: data.yearlyFormatted,
            perMonthFormatted: data.yearlyPerMonthFormatted,
            trialDays: data.yearlyTrialDays,
            savingsPercent: data.yearlySavingsPercent,
          }
        : null,
      source: 'iap',
      isLoading: false,
      error: null,
      isWebFallback: false,
    };
  }

  // Web: Use Stripe prices
  const data = stripePrices.data;

  if (stripePrices.isLoading) {
    return {
      monthly: null,
      sixMonth: null,
      yearly: null,
      source: 'stripe',
      isLoading: true,
      error: null,
      isWebFallback: false,
    };
  }

  if (stripePrices.error || !data) {
    // Fallback to hardcoded prices if Stripe fails
    return {
      monthly: { id: 'price_monthly', priceFormatted: '3,99 €' },
      sixMonth: {
        id: 'price_6month',
        priceFormatted: '14,99 €',
        perMonthFormatted: '2,50 €',
        trialDays: 7,
        savingsPercent: 37,
      },
      yearly: {
        id: 'price_1year',
        priceFormatted: '23,99 €',
        perMonthFormatted: '2,00 €',
        trialDays: 7,
        savingsPercent: 50,
      },
      source: 'stripe',
      isLoading: false,
      error: stripePrices.error as Error | null,
      isWebFallback: false,
    };
  }

  return {
    monthly: data.monthly
      ? { id: data.monthly.id, priceFormatted: data.monthlyFormatted }
      : { id: 'price_monthly', priceFormatted: data.monthlyFormatted },
    sixMonth: data.sixMonth
      ? {
          id: data.sixMonth.id,
          priceFormatted: data.sixMonthFormatted,
          perMonthFormatted: data.sixMonthPerMonthFormatted,
          trialDays: data.sixMonthTrialDays,
          savingsPercent: data.sixMonthSavingsPercent,
        }
      : {
          id: 'price_6month',
          priceFormatted: data.sixMonthFormatted,
          perMonthFormatted: data.sixMonthPerMonthFormatted,
          trialDays: data.sixMonthTrialDays,
          savingsPercent: data.sixMonthSavingsPercent,
        },
    yearly: data.yearly
      ? {
          id: data.yearly.id,
          priceFormatted: data.yearlyFormatted,
          perMonthFormatted: data.yearlyPerMonthFormatted,
          trialDays: data.yearlyTrialDays,
          savingsPercent: data.yearlySavingsPercent,
        }
      : {
          id: 'price_1year',
          priceFormatted: data.yearlyFormatted,
          perMonthFormatted: data.yearlyPerMonthFormatted,
          trialDays: data.yearlyTrialDays,
          savingsPercent: data.yearlySavingsPercent,
        },
    source: 'stripe',
    isLoading: false,
    error: null,
    isWebFallback: false,
  };
}

/**
 * Hook to initiate a purchase with the appropriate payment method
 * Returns functions to purchase each plan
 */
export function usePurchase() {
  // This will be implemented to handle both Stripe checkout and IAP
  // For now, returning null - components should use useCreateCheckout for Stripe
  // and usePurchaseIAP for native
  return {
    isNative,
    isWeb,
  };
}
